import { cn } from "@/lib/utils";
import {
  captureReactErrorBoundaryException,
  isPageOpenRecoveryError,
} from "@/lib/posthogExceptions";
import { attemptClientReloadRecovery } from "@/lib/reloadRecovery";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isRecovering: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isRecovering: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isRecovering: isPageOpenRecoveryError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isPageOpenRecoveryError(error)) {
      const recoveryStarted = attemptClientReloadRecovery(error.message);
      if (recoveryStarted) return;
      this.setState({ isRecovering: false });
    }

    captureReactErrorBoundaryException(error, errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isRecovering) {
        return (
          <div className="flex items-center justify-center min-h-screen p-8 bg-background">
            <div className="flex flex-col items-center gap-4">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-muted-foreground">Refreshing...</p>
            </div>
          </div>
        );
      }

      const showStack = import.meta.env.DEV;
      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
            />

            <h2 className="text-xl mb-4">An unexpected error occurred.</h2>

            <div className="p-4 w-full rounded bg-muted overflow-auto mb-6">
              {showStack ? (
                <pre className="text-sm text-muted-foreground whitespace-break-spaces">
                  {this.state.error?.stack}
                </pre>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Please reload the page. If this keeps happening, contact support.
                </p>
              )}
            </div>

            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 cursor-pointer"
              )}
            >
              <RotateCcw size={16} />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
